import webviewFun from '@/utils/webview-fun';
import cal from './cal';
import constant from '@/constant';
import storageUtil from '@/utils/storage'
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getConfig } from '@/config'
import iconGirl from '@/assets/img/icon-girl@2x.webp';
import iconBoy from '@/assets/img/icon-boy@2x.webp';
import iconSecret from '@/assets/img/icon-secret@2x.webp';

dayjs.extend(utc); // 加载 UTC 插件
dayjs.extend(timezone); // 加载时区插件

const { getUserInfo } = webviewFun;
const { common } = constant;
const { REQUEST_EXPIRED, DATE_TIME_FORMAT, DATE_FORMAT, DATE_API_FORMAT } = common;
const conf = getConfig()

/*
 * 将Promisr的结果和错误，转化为一个数组返回。
 * @params {Promise}
 * @return {array} [err, res]
 */
const to = (promise: Promise<any>) =>
  promise.then((res) => [null, res]).catch((err) => [err, null])
/**
 *  延迟一定时间，单位毫秒。
 */
const sleep = async (time: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, time);
  });

// 设置 accesstoken 和 uid
const setUidToken = async function (query: any) {
  let accesstoken = null;
  if (query.accesstoken && query.accesstoken != '') {
    accesstoken = query.accesstoken;
  } else {
    let userInfo = await getUserInfo();
    if (
      userInfo &&
      userInfo.res &&
      userInfo.res.accessToken &&
      userInfo.res.accessToken != ''
    ) {
      accesstoken = userInfo.res.accessToken;
    }
  }

  storageUtil.setSessionStorage('accessToken', accesstoken);
  return accesstoken;
};

/**
 * 数字转换
 * @param {*} number
 * @param {*} decimalNum 小数位数
 * @returns
 */
const numberThumbnailUnit = (number: number, decimalNum: number = 1) => {
  if (number < 1000) {
    return number;
  }

  if (number >= Math.pow(10, 3) && number <= 999999) {
    const codyNum = cal.divide(number, Math.pow(10, 3));
    const decimalIndex = String(codyNum).indexOf('.');
    // 只显示一位小数
    if (decimalIndex > -1) {
      return String(codyNum).substring(0, decimalIndex + decimalNum + 1) + 'K';
    }
    return codyNum + 'K';
  }

  if (number >= Math.pow(10, 6) && number <= 999999999) {
    const codyNum = cal.divide(number, Math.pow(10, 6));
    const decimalIndex = String(codyNum).indexOf('.');
    // 只显示一位小数
    if (decimalIndex > -1) {
      return String(codyNum).substring(0, decimalIndex + decimalNum + 1) + 'M';
    }
    return codyNum + 'M';
  }

  if (number > 999999999) {
    return '999.9M+';
  }
};

/**
 * 数字转换-数据中心用
 * @param {*} num
 * @returns
 */
const dataCenterNumberThumbnail = (num: number) => {
  if (num < Math.pow(10, 6)) {
    return numberThumbnail(num);
  }

  if (num >= Math.pow(10, 6) && num <= 999999999) {
    const codyNum = cal.divide(num, Math.pow(10, 6));
    const decimalIndex = String(codyNum).indexOf('.');
    // 只显示一位小数
    if (decimalIndex > -1) {
      return String(codyNum).substring(0, decimalIndex + 2) + 'M';
    }
    return codyNum + 'M';
  }

  if (num > 999999999) {
    return '999.9M+';
  }
};

const numberThumbnail = (num: number | string) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// 获取文件后缀
const getFileExtension = (url: string) => {
  try {
    // 从 URL 中提取文件名部分
    const filename = url.substring(url.lastIndexOf('/') + 1);
    // 确保 URL 包含文件名
    if (filename.indexOf('.') === -1) {
      throw new Error('No file extension found');
    }
    // 从文件名中提取后缀名
    const extension = filename.substring(filename.lastIndexOf('.') + 1);

    return extension;
  } catch (error) {
    console.error('Invalid URL', error);
  }
};

// 获取媒体类型
const getMediaType = (showType: string, showUrl: string) => {
  if (showType) return showType;
  return getFileExtension(showUrl);
};

// 获取url参数
function getUrlParameters(url: string) {
  if (!url) return;
  const paramsObject: { [key: string]: string } = {};

  // 找到问号后面的部分
  const queryStringIndex = url.indexOf('?');
  if (queryStringIndex !== -1) {
    const queryString = url.slice(queryStringIndex + 1);

    // 分割参数
    const pairs = queryString.split('&');

    pairs.forEach((pair) => {
      const [key, value] = pair.split('=');
      if (key) {
        // 解码参数并添加到对象中
        paramsObject[decodeURIComponent(key)] = value
          ? decodeURIComponent(value)
          : '';
      }
    });
  }

  return paramsObject;
}

function objectToQueryString(obj: any) {
  return new URLSearchParams(obj).toString();
}

// 检测是否支持webp
const checkWebPFeature = async (feature: 'lossy' | 'lossless' | 'alpha') => {
  const kTestImages = {
    lossy: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
    lossless: 'UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==',
    alpha:
      'UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==',
    animation:
      'UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA',
  };

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = function () {
      const result = img.width > 0 && img.height > 0;
      resolve(result);
    };
    img.onerror = function () {
      resolve(false);
    };
    img.src = 'data:image/webp;base64,' + kTestImages[feature];
  });
};

interface ILangItem {
  name: string;
  image_url: string;
  name_en: string;
  image_url_en: string;
  [key: string]: any;
}

const getLangItemName = (item: ILangItem) => {
  const language = storageUtil.getLocalStorage('language') || 'en';
  return item['name_' + language] || item.name;
};

const getLangImgUrl = (item: ILangItem) => {
  const language = storageUtil.getLocalStorage('language') || 'en';
  return item['image_url_' + language] || item.image_url;
};

/**
 * 是否为Null类型
 *
 * @param {*} obj
 * @returns
 */
function isNull(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Null]'
}

/**
 * 是否为Undefined类型
 *
 * @param {*} obj
 * @returns
 */
function isUndefined(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Undefined]'
}

/**
 * 数据是否为空
 * @param obj
 * @returns
 */
function isExist(obj: any) {
  return !isNull(obj) && !isUndefined(obj)
}

/**
 * 是否为空对象
 *
 * @param {*} obj
 * @returns
 */
function isEmptyObject(obj: any) {
  return isObject(obj) && Object.keys(obj).length === 0
}

/**
 * 是否为空数组
 *
 * @param {*} arr
 * @returns
 */
function isEmptyArray(arr: any) {
  return isArray(arr) && arr.length === 0
}

/**
 * 是否为Array类型
 *
 * @param {*} obj
 * @returns
 */
function isArray(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Array]'
}

/**
 * 是否为Object类型
 *
 * @param {*} obj
 * @returns
 */
function isObject(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Object]'
}
// 比较版本
// 返回 0 如果两个版本相等。
// 返回 1 如果第一个版本大于第二个版本。
// 返回 -1 如果第一个版本小于第二个版本。
function compareVersion(version1: string, version2: string) {
  const v1 = version1.split('.').map(Number);
  const v2 = version2.split('.').map(Number);

  const length = Math.max(v1.length, v2.length);

  for (let i = 0; i < length; i++) {
    const num1 = v1[i] || 0; // 如果没有该部分，默认为 0
    const num2 = v2[i] || 0; // 如果没有该部分，默认为 0

    if (num1 > num2) {
      return 1;
    }
    if (num1 < num2) {
      return -1;
    }
  }

  return 0;
}

/**
 * 获取url中的查询参数
 * @returns
 */
function getQuerySearch() {
  const queryString = window.location?.search || ''
  return queryString
}

/**
 * 解决请求竞态的问题，只有最后一次请求返回请求结果
 * @param {*} fn
 * @returns
 */
function onlyLastRequest(fn: (args: any) => Promise<any>) {
  let requestTime = 0

  return function requestFn(...args: any[]) {
    return new Promise((resolve, reject) => {
      const currentRequestTime = Date.now()
      requestTime = currentRequestTime

      fn(args)
        .then((res) => {
          if (currentRequestTime !== requestTime) {
            reject(REQUEST_EXPIRED)
            return
          }

          resolve(res)
        })
        .catch(reject)
    })
  }
}

/**
 * 函数防抖
 *
 * @param {function} fn 要执行的函数
 * @param {number} wait 等待时间
 * @param {boolean} immediate 是否立即执行，immediate为true时：立即执行，停止触发n秒后，才可以重新触发执行。immediate为false时：停止触发n秒后才执行
 * @returns {function}
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function debounce(fn: Function, ...args1: any[]): (...args: any[]) => void {
  let [wait, immediate] = args1

  if (wait === void 0) {
    wait = 500
    immediate = false
  }

  if (immediate === void 0) {
    immediate = false
  }

  let timer: any = null

  return function debounceFn(...args2: any[]) {
    if (timer !== null) {
      clearTimeout(timer)
    }

    if (immediate) {
      const callNow = timer === null
      timer = setTimeout(() => {
        timer = null
      }, wait)

      if (callNow) {
        fn.apply(fn, args2)
      }
    } else {
      timer = setTimeout(() => {
        fn.apply(fn, args2)
        timer = null
      }, wait)
    }
  }
}

/**
 * 函数节流
 *
 * 函数在一段时间内多次触发只会执行第一次，在这段时间结束前，不管触发多少次也不会执行函数。
 *
 * @param {*} fn
 * @param {number} [gapTime=500]
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function throttle(
  fn: (...args: any[]) => any,
  gapTime: number = 500,
): (...args: any[]) => void {
  let lastTime: number = 0

  // 返回新的函数
  return function throttleFn(...args: any[]) {
    const nowTime: number = Date.now()

    // 函数执行条件
    if (!lastTime || nowTime - lastTime > gapTime) {
      fn.apply(fn, args) // 将this和参数传给原函数
      lastTime = nowTime
    }
  }
}

/**
 * 复制文本到剪贴板
 *
 * @param {string} text
 */
function copyToClipboard(text: string) {
  const textArea = document.createElement('textarea');

  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
}

// 解析为沙特时间
function formatToShamsiDateTime(date?: dayjs.ConfigType, format = DATE_TIME_FORMAT): string {
  return dayjs(date).tz('Asia/Riyadh').format(format);
}

/**
 * 格式化日期
 * @param date 日期对象，支持 dayjs 支持的各种格式
 * @param format 格式化字符串，默认为 DATE_FORMAT
 * @returns 格式化后的日期字符串 DD/MM/YYYY
 */
function formatDate(date?: dayjs.ConfigType, format = DATE_FORMAT): string {
  return dayjs(date).format(format);
}

/**
 * 格式化日期
 * @param date 日期对象，支持 dayjs 支持的各种格式
 * @param format 格式化字符串，默认为 DATE_TIME_FORMAT
 * @returns 格式化后的日期字符串 DD/MM/YYYY HH:mm:ss
 */
function formatDateTime(date?: dayjs.ConfigType, format = DATE_TIME_FORMAT): string {
  return dayjs(date).format(format);
}

/**
 * 获取几天前的日期
 * @param day 天数，默认为 0
 * @param format 格式化字符串，默认为 DATE_API_FORMAT
 * @returns 格式化后的日期字符串 DD/MM/YYYY
 */
function dayAgo(day: number = 0, format = DATE_API_FORMAT) {
  return dayjs().subtract(day, 'day').format(format);
}

/**
 * 获取距离明天凌晨0点还有多少秒
 * @param serverTime 服务器返回的时间字符串或时间戳
 */
function getSecondsToMidnightToUTC3(serverTime: number) {
  const now = new Date(serverTime);
  
  // 创建表示明天凌晨0点的Date对象
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  
  // 计算时间差（毫秒）并转换为秒
  const diffInSeconds = Math.floor(cal.divide(cal.minus(midnight.getTime(), now.getTime()), 1000));

  // 加上5小时，得到距离明天凌晨5点的秒数 UTC+3
  return cal.plus(diffInSeconds, 5 * 60 * 60);
}

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * 获取url中的文件扩展名
 *
 * @param {string} url
 * @param {string} [defaultExt='jpg']
 * @returns
 */
function getExtName(url: string, defaultExt = '.jpg') {
  const matchResult = url.match(/(\.(?:.*?))$/);

  if (!matchResult) {
    return defaultExt;
  }

  return matchResult[1];
}

function isRtl(language: string): boolean {
  return ['ar', 'he', 'fa', 'ps', 'ur'].includes(language);
}

// 将毫秒换算成天数
function convertMillisecondsToDays(ms: number) {
  let days = ms / (1000 * 60 * 60 * 24);
  return days < 1 ? Number(days.toFixed(1)) : Math.floor(days);
}

// 判断是否是 voca 项目
function isVoca() {
  if (['vocaDev', 'vocaTesting', 'vocaGray', 'vocaProd'].includes(conf.realEnv)) {
    return true
  }
  return false
}

// 判断是否是俄罗斯域名
function isRussiaHost() {
  const hostname = window.location.hostname;
  return hostname.toLowerCase().endsWith('.hobychat.ru');
}

function isProduction() {
  const NODE_ENV = import.meta.env.MODE
  // 判断是否是生产环境的条件，打包是生产环境，或者app传的环境是生产环境
  return ['vocaProd', 'production'].includes(NODE_ENV)
}

function getGenderIcon(gender: number) {
  switch (gender) {
    case 1:
      return iconBoy
    case 2:
      return iconGirl
    default:
      return iconSecret
  }
}

// 检测请求接口域名是否在 conf.rootPtah 中
function checkRequestHost(url: string) {
  if (!url) return false
  const rootPath = conf.rootPath
  const requestHost = new URL(url).hostname
  return rootPath.indexOf(requestHost) > -1
}

function getLevel(level: number) {
  if (level <= 10) return 10;
  
  // 其他情况：向上取整到最近的10的倍数
  return Math.ceil(level / 10) * 10;
}

// 生成 levelIcon 地址
function createdLevelIconUrl(url: string, type: 'wealth' | 'charm' | 'recharge' | 'game' | 'lucky', level: number, size: 1|2|3 = 2) {
  if (url.length === 0) return
  const activeLevel = getLevel(level);
  return url.replace('{type}', type).replace('{level}', activeLevel.toString()).replace('{size}', size.toString())
}

// 生成 levelLabel 地址
function createdLevelLabelUrl(url: string, language: string, type: 'wealth' | 'charm' | 'recharge' | 'game' | 'lucky', level: number, size: 1|2|3 = 2) {
  if (url.length === 0) return
  const direction = isRtl(language)? 'r' : 'l';
  let activeLevel = getLevel(level);
  return url.replace('{type}', type).replace('{ltr}', direction).replace('{level}', activeLevel.toString()).replace('{size}', size.toString())
}

// 生成所有 levelIcon 地址
function createdAllLevelIconUrl(url: string, type: 'wealth' | 'charm' | 'recharge' | 'game' | 'lucky', size: 1 | 2 | 3 = 2) {
  if (url.length === 0) return
  const levels = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const urls = levels.map(level => {
    return {
      url: createdLevelIconUrl(url, type, level, size),
      name: level,
    }
  });
  return urls
}

export default {
  to,
  sleep,
  setUidToken,
  numberThumbnailUnit,
  numberThumbnail,
  getFileExtension,
  getMediaType,
  checkWebPFeature,
  getUrlParameters,
  objectToQueryString,
  getLangItemName,
  getLangImgUrl,
  compareVersion,
  isArray,
  isEmptyArray,
  isEmptyObject,
  isObject,
  isNull,
  isUndefined,
  isExist,
  getQuerySearch,
  onlyLastRequest,
  debounce,
  throttle,
  copyToClipboard,
  formatToShamsiDateTime,
  getSecondsToMidnightToUTC3,
  dataCenterNumberThumbnail,
  isIOS,
  formatDate,
  formatDateTime,
  dayAgo,
  getExtName,
  isRtl,
  convertMillisecondsToDays,
  isVoca,
  isRussiaHost,
  isProduction,
  getGenderIcon,
  checkRequestHost,
  createdLevelIconUrl,
  createdLevelLabelUrl,
  createdAllLevelIconUrl,
  getLevel
};
